/*
	Soulog · Smooth same-page anchor scrolling
	---------------------------------------------------------------------------
	Purpose: the five submenu items in the header's "Support" dropdown point to different sections of the same page
	(/pages/service-and-support#download etc.). On click, smoothly scroll to the matching section, and keep the section
	heading from being covered by the sticky header.

	Why this must be JS rather than CSS scroll-behavior:smooth:
	  assets/soulog-theme-fixes.css §4 uses !important to switch off scroll-behavior on
	  html / #wrapper — turning it on makes all site-wide GSAP ScrollTrigger animations break entirely after a
	  resize (measured 2026-07-24: start is off by exactly one scrollY). That rule is guarded by the regression test
	  tests/unit/scroll-behavior-guard.test.mjs and must not be reverted.
	  So smoothness can only be passed explicitly here as behavior:'smooth' (behavior in the options takes precedence over
	  CSS scroll-behavior and is unaffected by that !important).

	Why compare only pathname, not host:
	  Menu links in the Shopify admin are stored as absolute URLs (https://www.soulog.com/pages/...#faq),
	  while the local preview runs on 127.0.0.1:9292, and production may have multiple domains. Comparing host would break
	  local preview and multi-domain setups outright. Cross-site links with the same path + a hash don't exist in practice, so only path is compared.

	Header offset: no hardcoded height; the actual height of .header is measured at runtime — it differs across breakpoints,
	before/after sticking, and with/without the promo bar. When position is not sticky/fixed
	(header not sticky) the offset is 0.
*/
(function () {
	var EXTRA_GAP = 12; // a little extra breathing room above the section so the heading doesn't hug the header's bottom edge

	function headerOffset() {
		var header = document.querySelector(".header");
		if (!header) return 0;
		var position = window.getComputedStyle(header).position;
		if (position !== "sticky" && position !== "fixed") return 0;
		return header.getBoundingClientRect().height;
	}

	function prefersReducedMotion() {
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	}

	function scrollToTarget(target, smooth) {
		var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - EXTRA_GAP;
		window.scrollTo({
			top: Math.max(top, 0),
			behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto",
		});
	}

	function targetFromHash(hash) {
		if (!hash || hash.length < 2) return null;
		var id;
		try {
			id = decodeURIComponent(hash.slice(1));
		} catch (e) {
			id = hash.slice(1);
		}
		return document.getElementById(id);
	}

	/*
		Both of these exclusions are required — there are other href="#..." links on the site, and taking them over causes real bugs:

		· .screen-reader-shortcut (the "skip to main content" link at layout/theme.liquid:312):
		  the browser's native anchor jump **also moves focus to the target**; after preventDefault focus stays put,
		  so for keyboard users pressing the skip link does nothing at all. Such links must be left to the browser.

		· Target element's offsetParent is null: it is display:none, has a hidden ancestor, or is position:fixed
		  — the typical case is the subscribe popup #SubscribePopup (assets/subscribe-popup.js takes it over with the same document
		  delegation and calls preventDefault). Such an element is not in the page flow, its measured position is meaningless,
		  and scrolling to it would only make the page jump inexplicably.
	*/
	function shouldSkip(link, target) {
		if (link.classList.contains("screen-reader-shortcut")) return true;
		if (target.offsetParent === null) return true;
		return false;
	}

	/*
		After clicking an anchor in the nav, the dropdown must be closed — normally clicking a menu item navigates away and the panel disappears with the new page;
		a same-page anchor doesn't navigate, so two things both keep it hanging open:

		  · the panel is driven by CSS :hover (app.css:2995); after the click the mouse is still over the panel → stays hovered
		  · after preventDefault focus is still on that link, and :focus-within (app.css:3064) also holds it open

		So both are needed: add a dismissed class to the menu item (CSS forces it closed, see
		soulog-theme-fixes.css §5), and blur the link. The class is removed when the mouse leaves that menu item,
		so the next hover expands it as usual.
	*/
	var DISMISSED_CLASS = "soulog-menu-dismissed";

	function dismissMenu(link) {
		var item = link.closest("li.menu-item-has-children");
		if (!item) return;
		item.classList.add(DISMISSED_CLASS);
		item.addEventListener(
			"mouseleave",
			function () {
				item.classList.remove(DISMISSED_CLASS);
			},
			{ once: true },
		);
		if (typeof link.blur === "function") link.blur();
	}

	document.addEventListener("click", function (event) {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
		var link = event.target.closest && event.target.closest('a[href*="#"]');
		if (!link || link.target === "_blank") return;
		if (link.pathname !== window.location.pathname) return; // navigating to another page — leave it to the browser
		var target = targetFromHash(link.hash);
		if (!target || shouldSkip(link, target)) return;

		event.preventDefault();
		dismissMenu(link);
		scrollToTarget(target, true);
		if (window.history && window.history.pushState) {
			window.history.pushState(null, "", link.hash);
		}
	});

	// Arriving from another page with #faq: wait until the page has loaded (positions are only accurate after images expand the height) and scroll once more
	window.addEventListener("load", function () {
		var target = targetFromHash(window.location.hash);
		if (target && target.offsetParent !== null) scrollToTarget(target, false);
	});
})();
